import { ClientDataType } from "@/api/ClientApi";
import { ACTIVITY_CODE_LENGTH } from "@utils/Constants";
import { detectIdentificationType, normalizeIdentification } from "@utils/Identification";
import { normalizePhoneNumber } from "@utils/Phone";
import { ClientImportField, ImportValueKind, findClientImportField } from "./clientImportFields";
import { clean, parseCashCreditType, parseImportBoolean, parseImportInteger } from "./core/values";
import { ColumnMapping as GenericColumnMapping } from "./core/types";

export { parseImportBoolean, parseImportInteger };

/** Codigo de pais que se asume cuando el archivo no trae la columna (Costa Rica). */
export const DEFAULT_PHONE_COUNTRY_CODE = "506";

/** Columna del archivo -> atributo del cliente. `null` = columna sin mapear. */
export type ColumnMapping = GenericColumnMapping & Record<number, ClientImportField["name"] | null>;

/**
 * Normaliza el codigo de actividad economica.
 *
 * Los CSV con configuracion regional en espanol exportan el decimal con coma
 * ("4752,1") y Hacienda lo espera con punto. Ademas, los codigos son siempre de 6
 * caracteres (ver ACTIVITY_CODE_LENGTH), pero los archivos suelen traerlos recortados a
 * 4 digitos: se completan con ".0" ("4530" -> "4530.0"). Sin eso, el cliente importado
 * no pasa la validacion del formulario cuando factura electronicamente.
 */
export const normalizeActivityCode = (raw: unknown): string => {
    const value = clean(raw).replace(",", ".");
    if (!value) return "";

    if (/^\d{4}$/.test(value)) return `${value}.0`;

    return value.length > ACTIVITY_CODE_LENGTH ? value.slice(0, ACTIVITY_CODE_LENGTH) : value;
};

/** "Contado" -> 0, "Credito" -> 1. Devuelve `undefined` si el valor no se reconoce. */
export const parseClientType = parseCashCreditType;

/** Aplica la normalizacion que corresponde al tipo del atributo destino. */
export const normalizeImportValue = (kind: ImportValueKind, raw: unknown): unknown => {
    switch (kind) {
        case "identification":
            return normalizeIdentification(`${raw ?? ""}`);
        case "phone":
        case "phoneCountryCode":
            return normalizePhoneNumber(`${raw ?? ""}`);
        case "email":
            return clean(raw).toLowerCase();
        case "integer":
            return parseImportInteger(raw);
        case "boolean":
            return parseImportBoolean(raw);
        case "clientType":
            return parseClientType(raw);
        case "activityCode":
            return normalizeActivityCode(raw);
        case "code":
        case "zoneName":
        case "text":
        default:
            return clean(raw);
    }
};

/**
 * Aplica el mapeo de columnas a una fila cruda y devuelve los atributos del cliente.
 *
 * La zona se deja como nombre en `zoneName`: resolverla a `zoneId` requiere crear las
 * zonas que falten, y eso lo hace `resolveImportRows` con la API.
 */
export const mapRowToClient = (
    row: string[],
    mapping: ColumnMapping
): { client: ClientDataType; zoneName: string } => {
    const client: Record<string, unknown> = {};
    let zoneName = "";

    Object.entries(mapping).forEach(([columnIndex, fieldName]) => {
        if (!fieldName) return;

        const field = findClientImportField(fieldName);
        if (!field) return;

        const value = normalizeImportValue(field.kind, row[Number(columnIndex)]);

        if (field.kind === "zoneName") {
            zoneName = `${value ?? ""}`;
            return;
        }

        // Un valor vacio no debe pisar el default del modelo, salvo los booleanos, donde
        // "no" es informacion legitima.
        if (value === undefined || value === "") return;

        client[fieldName] = value;
    });

    // El archivo no trae el tipo de identificacion: se deduce del formato del numero.
    const tipoIdentificacion = detectIdentificationType(`${client.identificacion ?? ""}`);
    if (tipoIdentificacion) {
        client.tipo_identificacion = tipoIdentificacion;
    }

    // Sin columna de codigo de pais se asume Costa Rica, que es el formato que ya usa
    // el resto del sistema al armar el XML de Hacienda.
    if (!client.telefono1_cd) client.telefono1_cd = DEFAULT_PHONE_COUNTRY_CODE;
    if (!client.telefono2_cd) client.telefono2_cd = DEFAULT_PHONE_COUNTRY_CODE;

    return { client: client as ClientDataType, zoneName };
};
